<template>
  <div class="emh-selected-filters">
    <button
      v-if="selectedFiltersFormatted.length > 0 && isMobile"
      id="emh-selected-filters-reset-button"
      class="emh-selected-filters__clear-all wb-link wb-link--standalone wb-link--inline"
      data-test-id="emh-selected-filters-clear-all"
      @click="onClearAllFilters()"
    >
      <span data-test-id="emh-selected-filters-reset">
        {{ $t('dcp.assortment.cars.filtersSearch.filters.resetAlt') }} ({{ selectedFiltersFormatted.length }})
      </span>
    </button>
    <div
      v-for="filter in visibleFilters"
      :key="filter.code"
      class="emh-selected-filters__pill"
      data-test-id="emh-selected-filters-tag"
    >
      <ui-tag variant="tertiary" removable @remove="onRemoveFilter(filter)">
        <span v-if="filter.filterName" class="emh-selected-filters__tag-prefix"> {{ filter.filterName }}:&nbsp; </span>
        <span class="emh-selected-filters__tag-label">
          <img
            v-if="filter.filterCode === FilterCode.brand"
            style="height: 16px"
            :src="getBrandFilterUrl(filter)"
            :alt="filter.filterLabel"
          />
          <div v-else>{{ truncate(filter.filterLabel, viewPortService.isSmallerMobile() ? 12 : 50) }}</div>
        </span>
      </ui-tag>
    </div>

    <button
      v-if="selectedFiltersFormatted.length > maxItemsNumber"
      class="emh-selected-filters__show-more wb-link wb-link--standalone"
      data-test-id="emh-selected-filters-show-more"
      @click="onToggleShowAllFilters()"
    >
      <span data-test-id="emh-selected-filters-reset">
        {{ $t(showAllFilters ? 'ui.form.checkbox.showLess' : 'ui.form.checkbox.showMore') }}
        {{ showAllFilters ? '' : `(${selectedFiltersFormatted.length - maxItemsNumber})` }}
      </span>
    </button>
    <button
      v-if="selectedFiltersFormatted.length > 0 && !isMobile"
      id="emh-selected-filters-reset-button"
      class="emh-selected-filters__clear-all wb-link wb-link--standalone wb-link--inline"
      data-test-id="emh-selected-filters-clear-all"
      @click="onClearAllFilters()"
    >
      <span data-test-id="emh-selected-filters-reset">
        {{ $t('dcp.assortment.cars.filtersSearch.filters.resetAlt') }} ({{ selectedFiltersFormatted.length }})
      </span>
    </button>
  </div>
</template>

<script lang="ts">
export default { name: 'EmhSelectedFilterPills' };
</script>

<script lang="ts" setup>
import { useTracking } from '@dh-io-emh/shared/src/composables/tracking.composable';
import { FilterCode, ProductFilterTypes } from '@dh-io-emh/shared/src/models/product-filter/product-filter-base.model';
import { IProductFilterType } from '@dh-io-emh/shared/src/models/product-filter/product-filter.model';
import { useProductsStore } from '@dh-io-emh/shared/src/stores/products/products.composable';
import UiTag from '@dh-io-emh/ui/src/elements/tag/Tag.vue';
import { truncate } from '@dh-io-emh/ui/src/utils/string.util';
import { computed, PropType, ref, toRefs, watch } from 'vue';
import { Emit } from './selectedFiltersPill.type';

import { viewPortService } from '@dh-io-emh/ui/src/utils/viewport.util';

import { useBreakpoint } from '@dh-io-emh/shared/src/composables/breakpoint.composable';

const emit = defineEmits([Emit.ChangeFilter, Emit.ClearAllFilters]);
const props = defineProps({
  filters: {
    type: Array as PropType<Array<IProductFilterType>>,
    required: true,
  },
});

const { trackLinkClick, getProductsDataTrackingObject } = useTracking();
const productsStore = useProductsStore();
const { selectedFiltersFormatted } = toRefs(productsStore.selectors);
const { results: products } = toRefs(productsStore.state);
const { setLocationFilterParams, setDealerFilterInfo } = productsStore.dispatchers;

const showAllFilters = ref(false);
const maxItemsNumber = ref(7);

const { isMobile } = useBreakpoint();

const visibleFilters = computed(() => {
  if (showAllFilters.value) {
    return selectedFiltersFormatted.value;
  }

  return selectedFiltersFormatted.value.slice(0, maxItemsNumber.value);
});

function getBrandFilterUrl(filterOption) {
  return new URL(`../../assets/sideFilters/brands/${filterOption.filterLabel.toLowerCase()}.svg`, import.meta.url).href;
}

function onToggleShowAllFilters() {
  showAllFilters.value = !showAllFilters.value;
}

function onRemoveFilter(filterOption) {
  const selectedFilter = props.filters.find((filter) => filter.code === filterOption.filterCode);

  if (!selectedFilter) {
    return;
  }

  const stop = watch(
    () => products.value,
    () => {
      trackRemoveEvent(filterOption);
      stop();
    },
  );

  switch (selectedFilter.type) {
    case ProductFilterTypes.MultiSelect: {
      // Cover special filter locations: when filter is removed, all values should be reset
      // Because the filter button is "grouping" all values in one filter button
      if (selectedFilter.code === FilterCode.locations) {
        setDealerFilterInfo(undefined);
        setLocationFilterParams(undefined);
        emit(Emit.ChangeFilter, { filterCode: selectedFilter.code, filterValue: [] });
      }

      const selectedFilterValue = selectedFilter.value.slice();
      const selectedOptionIndex = selectedFilterValue.indexOf(filterOption.filterValue);

      if (selectedOptionIndex !== -1) {
        selectedFilterValue.splice(selectedOptionIndex, 1);

        emit(Emit.ChangeFilter, { filterCode: selectedFilter.code, filterValue: selectedFilterValue });
      }

      break;
    }
    case ProductFilterTypes.Slider:
    case ProductFilterTypes.PriceSlider:
    case ProductFilterTypes.Checkbox: {
      emit(Emit.ChangeFilter, { filterCode: selectedFilter.code, filterValue: selectedFilter.getResetValue() });

      break;
    }
  }
}

function onClearAllFilters() {
  showAllFilters.value = false;

  trackLinkClick({
    eventDetail: {
      category: 'filter',
      action: 'remove.filter_all_items',
      label: 'from_pills',
    },
  });

  emit(Emit.ClearAllFilters);
}

function trackRemoveEvent(filterOption) {
  trackLinkClick({
    eventDetail: {
      category: 'filter',
      action: `remove.filter_single_item_pill`,
      label: getTrackingActionLabel(filterOption),
      filter: selectedFiltersFormatted.value.map((filter) => ({
        type: filter.filterType,
        name: filter.filterCode,
        value: filter.filterTrackingValue || filter.filterValue || filter.filterLabel,
      })),
      products: getProductsDataTrackingObject(products.value),
    },
  });
}

function getTrackingActionLabel(filterOption) {
  // If the filter option has a tracking value, use it.
  if (filterOption.filterTrackingValue) {
    return `${filterOption.filterType}|${filterOption.filterCode}|${filterOption.filterTrackingValue}|0`;
  }

  // Otherwise, use the filter value, filter label, or the word 'undo'.
  return `${filterOption.filterType}|${filterOption.filterCode}|${
    filterOption.filterValue || filterOption.filterLabel || 'undo'
  }|0`;
}
</script>

<style lang="scss" scoped>
.emh-selected-filters {
  display: flex;
  gap: 0.5rem 0.5rem;
  flex-flow: row wrap;

  &__tag-label,
  &__tag-prefix {
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  &__tag-prefix {
    @include wb-type-hint;
  }

  wb-tag {
    max-width: none;
  }

  &__pill {
    cursor: default;
    @include breakpoint-to('mq3') {
      cursor: pointer;
    }
  }

  &__show-more {
    border: none;
    margin: 0 0.25rem;
    font-size: 14px;
    @include breakpoint-to('mq3') {
      white-space: nowrap;
    }
  }

  &__clear-all {
    border: none;
    margin: 0 0.25rem;
    font-size: 14px;
    @include breakpoint-to('mq3') {
      margin-right: 6px;
    }
  }

  @include breakpoint-to('mq3') {
    margin: var(--wb-spacing-m) auto;
    overflow-x: scroll;
    flex-wrap: nowrap;
    justify-content: flex-start;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  @include breakpoint-to('mq4') {
    margin-left: auto;
    margin-right: auto;
  }

  #emh-selected-filters-reset-button {
    @include breakpoint-to('mq3') {
      white-space: nowrap;
    }
  }

  &::-webkit-scrollbar {
    @include breakpoint-to('mq3') {
      display: none;
    }
  }
}
</style>
